import React, { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  IconButton,
  Drawer,
  Tabs,
  Tab,
  Box,
  Typography,
  Divider,
  Button,
  Chip,
  Select,
  MenuItem as SelectItem,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  CheckCircle as ConfirmIcon,
  LocalShipping as HandoverIcon,
  AssignmentReturn as ReturnIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Assessment as SummaryIcon,
  FiberNew as NewIcon,
} from '@mui/icons-material';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import SortIcon from '@mui/icons-material/Sort';
import FilterListIcon from '@mui/icons-material/FilterList';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import CarRentalIcon from '@mui/icons-material/CarRental';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';

import NotificationService, { initNotificationSocket } from '../services/notificationService';
import alertService, { DashboardAlert } from '../services/alertService';
import expenseService from '../services/expenseService';
import AlertList from './AlertList';
import { usePermissions } from '../hooks/usePermissions';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../store/authStore';
import { translateNotificationContent } from '../utils/notificationLocalization';

// Ripple pulse (keeps original)
const RipplePulse = styled(Box)(() => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '100%',
  height: '100%',
  borderRadius: '50%',
  border: '2px solid #d32f2f',
  animation: 'ripple-pulse 1.5s cubic-bezier(0, 0, 0.2, 1) infinite',
  pointerEvents: 'none',
  '@keyframes ripple-pulse': {
    '0%': {
      transform: 'translate(-50%, -50%) scale(0.8)',
      opacity: 1,
    },
    '100%': {
      transform: 'translate(-50%, -50%) scale(2.5)',
      opacity: 0,
    },
  },
}));

type SortOption = 'newest' | 'oldest' | 'type';

const NotificationBell: React.FC = () => {
  const { hasPermission } = usePermissions();
  const canViewAlerts = hasPermission('alerts.view');
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, employee, userType } = useAuthStore();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // 0 = Alerts (persistent, actionable), 1 = Unread (transient), 2 = History
  const [tab, setTab] = useState<number>(0);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [unreadList, setUnreadList] = useState<any[]>([]);
  const [historyList, setHistoryList] = useState<any[]>([]);
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [filterType, setFilterType] = useState<string>('all');

  // Persistent alerts (the unified bell now surfaces both the live events
  // above AND actionable alerts from the Alert collection).
  const [alerts, setAlerts] = useState<DashboardAlert[]>([]);
  const [dismissingAlertId, setDismissingAlertId] = useState<string | null>(null);
  const [settlingAlertId, setSettlingAlertId] = useState<string | null>(null);

  const currentUserId = userType === 'employee'
    ? employee?.id || null
    : user?._id || null;
  const currentUserName = userType === 'employee'
    ? [employee?.firstName, employee?.lastName].filter(Boolean).join(' ').trim() || employee?.email || null
    : user?.businessName || user?.ownerInfo?.firstName || null;

  // load notifications into unread/history arrays
  const loadNotifications = () => {
    const all = NotificationService.getNotifications().map((n: any) => ({
      ...n,
      // ensure timestamp is Date
      timestamp: n.timestamp ? new Date(n.timestamp) : new Date(),
    }));

    const unread = all.filter((n: any) => !n.read);
    const read = all.filter((n: any) => n.read);

    setUnreadList(unread);
    setHistoryList(read);
    setUnreadCount(unread.length);
    setIsConnected(!!NotificationService.getSocket()?.connected);
  };

  const loadAlerts = () => {
    if (!canViewAlerts) { setAlerts([]); return; }
    alertService.list({ limit: 100 })
      .then(setAlerts)
      .catch((err) => console.error('Failed to load alerts in bell:', err));
  };

  useEffect(() => {
    initNotificationSocket();
    loadNotifications();
    loadAlerts();

    const reload = () => loadNotifications();
    window.addEventListener('notification-added', reload as any);
    window.addEventListener('notifications-updated', reload);

    // Subscribe to backend alert mutations so the bell badge updates in
    // real-time. Backend emits alert:new on first reconcile, alert:updated
    // on snooze/assign/etc., and alert:resolved on dismiss or auto-resolve.
    const socket = NotificationService.getSocket();
    const refresh = () => loadAlerts();
    if (socket) {
      socket.on('alert:new', refresh);
      socket.on('alert:updated', refresh);
      socket.on('alert:resolved', refresh);
    }

    return () => {
      window.removeEventListener('notification-added', reload as any);
      window.removeEventListener('notifications-updated', reload);
      if (socket) {
        socket.off('alert:new', refresh);
        socket.off('alert:updated', refresh);
        socket.off('alert:resolved', refresh);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewAlerts]);

  const handleDismissAlert = async (alert: DashboardAlert) => {
    if (!alert?._id) return;
    setDismissingAlertId(alert._id);
    setAlerts((prev) => prev.filter((a) => a._id !== alert._id));
    try { await alertService.dismiss(alert._id); }
    catch (err) {
      console.error('Failed to dismiss alert from bell:', err);
      setAlerts((prev) => (prev.some((a) => a._id === alert._id) ? prev : [alert, ...prev]));
    } finally { setDismissingAlertId(null); }
  };

  // Inline "Mark paid" on a vehicle recurring-bill alert: settle this cycle
  // (creates the paid expense + advances the schedule). The alert auto-resolves
  // once the schedule moves past the 30-day window, so we just reload after.
  const handleSettleAlert = async (alert: DashboardAlert) => {
    if (!alert?._id || !alert.source?.refId) return;
    setSettlingAlertId(alert._id);
    try {
      await expenseService.settleExpense(alert.source.refId);
      // Refresh finance surfaces that depend on expenses.
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['caisse-ledger'] });
      queryClient.invalidateQueries({ queryKey: ['revenue-lease'] });
      loadAlerts();
    } catch (err) {
      console.error('Failed to settle expense from notification:', err);
    } finally {
      setSettlingAlertId(null);
    }
  };

  const handleSnoozeAlert = async (alert: DashboardAlert, until: Date) => {
    if (!alert?._id) return;
    setDismissingAlertId(alert._id);
    setAlerts((prev) => prev.filter((a) => a._id !== alert._id));
    try { await alertService.snooze(alert._id, until); }
    catch (err) {
      console.error('Failed to snooze alert from bell:', err);
      setAlerts((prev) => (prev.some((a) => a._id === alert._id) ? prev : [alert, ...prev]));
    } finally { setDismissingAlertId(null); }
  };

  const handleAssignAlert = async (
    alert: DashboardAlert,
    assignee: { userId: string; name: string } | null,
  ) => {
    if (!alert?._id) return;
    setAlerts((prev) => prev.map((a) => (
      a._id === alert._id
        ? { ...a, assignedTo: assignee ? { userId: assignee.userId, name: assignee.name } : { userId: null, name: null } }
        : a
    )));
    try { await alertService.assign(alert._id, assignee); }
    catch (err) {
      console.error('Failed to assign alert from bell:', err);
      setAlerts((prev) => prev.map((a) => (a._id === alert._id ? alert : a)));
    }
  };

  const toggleDrawer = (open?: boolean) => {
    setDrawerOpen(open === undefined ? !drawerOpen : open);
  };

  const handleTabChange = (_: any, newValue: number) => {
    setTab(newValue);
  };

  const handleMarkAllRead = () => {
    NotificationService.markAllAsRead();
    loadNotifications();
  };

  // clearAll handler removed (unused)

  const handleMarkRead = (id: string) => {
    NotificationService.markAsRead(id);
    loadNotifications();
  };

  // Navigate to a transient notification's target via the SPA router (not a
  // full-page reload), then mark it read and close the drawer — mirrors how the
  // Alerts tab handles alert.href.
  const handleNotificationNavigate = (n: any) => {
    if (!n?.actionUrl) return;
    navigate(n.actionUrl);
    handleMarkRead(n.id);
    toggleDrawer(false);
  };

  const handleDelete = (id: string) => {
    NotificationService.clearNotification(id);
    loadNotifications();
  };

  const handleDeleteHistoryItem = (id: string) => {
    // same as single delete
    NotificationService.clearNotification(id);
    loadNotifications();
  };

  const applySortAndFilter = (items: any[]) => {
    let filtered = items;
    if (filterType !== 'all') {
      filtered = filtered.filter(i => i.type === filterType);
    }

    if (sortBy === 'newest') {
      filtered = filtered.sort((a, b) => +new Date(b.timestamp) - +new Date(a.timestamp));
    } else if (sortBy === 'oldest') {
      filtered = filtered.sort((a, b) => +new Date(a.timestamp) - +new Date(b.timestamp));
    } else if (sortBy === 'type') {
      filtered = filtered.sort((a, b) => (a.type || '').localeCompare(b.type || ''));
    }

    return filtered;
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'NEW_BOOKING':
      case 'booking_update':
        return <NewIcon />;
      case 'BOOKING_CONFIRMED':
        return <ConfirmIcon />;
      case 'VEHICLE_HANDOVER':
      case 'handover_delayed':
        return <HandoverIcon />;
      case 'VEHICLE_RETURN':
        return <ReturnIcon />;
      case 'BOOKING_CANCELLED':
        return <CancelIcon />;
      case 'OVERDUE_ALERT':
      case 'collection_overdue':
        return <WarningIcon />;
      case 'DAILY_SUMMARY':
        return <SummaryIcon />;
      case 'rental_ending':
        return <AccessTimeIcon />;
      case 'COLLECTION_UPCOMING':
      case 'collection_upcoming':
        return <CarRentalIcon />;
      case 'VEHICLE_MAINTENANCE':
      case 'vehicle_maintenance':
        return <BuildCircleIcon />;
      case 'vehicle_insurance':
        return <VerifiedUserIcon />;
      default:
        return <NotificationsIcon />;
    }
  };

  const getSeverityColor = (type: string, severity?: string) => {
    if (severity === 'error') return { bg: '#FEF2F2', border: '#FECACA', text: '#991B1B' };
    if (severity === 'warning') return { bg: '#FFFBEB', border: '#FDE68A', text: '#92400E' };
    if (severity === 'info') return { bg: '#EFF6FF', border: '#BFDBFE', text: '#1E40AF' };
    switch (type) {
      case 'handover_delayed':
      case 'OVERDUE_ALERT':
      case 'collection_overdue':
        return { bg: '#FEF2F2', border: '#FECACA', text: '#991B1B' };
      case 'rental_ending':
      case 'collection_upcoming':
      case 'vehicle_maintenance':
      case 'vehicle_insurance':
        return { bg: '#FFFBEB', border: '#FDE68A', text: '#92400E' };
      default:
        return { bg: '#f5f5f5', border: '#e0e0e0', text: '#333' };
    }
  };

  const formatTime = (timestamp: string | Date) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return t('notifications.justNow', { defaultValue: 'Just now' });
    if (minutes < 60) return t('notifications.minutesAgo', { defaultValue: '{{count}}m ago', count: minutes });
    if (hours < 24) return t('notifications.hoursAgo', { defaultValue: '{{count}}h ago', count: hours });
    return t('notifications.daysAgo', { defaultValue: '{{count}}d ago', count: days });
  };

  // Styled sizes & spacing variables
  const HEADER_HEIGHT = 96;
  const AVATAR_SIZE = 48;
  const ACTION_BTN_SIZE = 36; // generous action button size

  return (
    <>
      <IconButton color="inherit" onClick={() => toggleDrawer(true)} sx={{ position: 'relative' }}>
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          <Badge badgeContent={unreadCount + alerts.length} color="error">
            <NotificationsIcon
              sx={{
                color: '#808080',
                transition: '0.18s ease',
                '&:hover': { color: '#555', transform: 'scale(1.06)' },
                fontSize: 26,
              }}
            />
          </Badge>

          {unreadCount > 0 && <RipplePulse />}
        </Box>

        {/* connection circle */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 6,
            right: 6,
            width: 10,
            height: 10,
            borderRadius: '50%',
            bgcolor: isConnected ? '#5EBD81' : '#999999',
            border: '2px solid white',
          }}
        />
      </IconButton>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => toggleDrawer(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 540, md: 640 },
            borderRadius: 2,
            overflow: 'hidden',
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            height: HEADER_HEIGHT,
            px: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider',
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1 }}>
              {t('notifications.title', { defaultValue: 'Notifications' })}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t('notifications.summary', {
                defaultValue: '{{unread}} unread • {{total}} total',
                unread: unreadCount,
                total: NotificationService.getNotifications().length,
              })}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label={isConnected ? t('notifications.live', { defaultValue: 'Live' }) : t('notifications.offline', { defaultValue: 'Offline' })}
              size="medium"
              sx={{
                bgcolor: isConnected ? '#5EBD81' : '#BDBDBD',
                color: 'white',
                fontWeight: 700,
                height: 28,
              }}
            />

            <Button variant="outlined" size="small" onClick={handleMarkAllRead}>
              {t('notifications.markAllRead', { defaultValue: 'Mark all read' })}
            </Button>

            <Tooltip title={t('notifications.close', { defaultValue: 'Close' })}>
              <IconButton onClick={() => toggleDrawer(false)} size="large">
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        {/* Tabs & Controls */}
        <Box sx={{ px: 3, pt: 2 }}>
          <Tabs value={tab} onChange={handleTabChange}>
            <Tab label={t('notifications.alertsTab', { defaultValue: 'Alerts ({{count}})', count: alerts.length })} />
            <Tab label={t('notifications.unreadTab', { defaultValue: 'Unread ({{count}})', count: unreadList.length })} />
            <Tab label={t('notifications.historyTab', { defaultValue: 'History ({{count}})', count: historyList.length })} />
          </Tabs>

          <Divider sx={{ my: 2 }} />

          {tab === 0 && (
            <Box sx={{ pb: 2 }}>
              <AlertList
                alerts={alerts}
                onAlertClick={(alert) => {
                  if (alert.href) {
                    navigate(alert.href);
                    toggleDrawer(false);
                  }
                }}
                onDismissAlert={hasPermission('alerts.edit') ? handleDismissAlert : undefined}
                onSnoozeAlert={hasPermission('alerts.edit') ? handleSnoozeAlert : undefined}
                onAssignAlert={hasPermission('alerts.edit') ? handleAssignAlert : undefined}
                onSettleAlert={hasPermission('alerts.edit') ? handleSettleAlert : undefined}
                settlingAlertId={settlingAlertId}
                currentUserId={currentUserId}
                currentUserName={currentUserName}
                dismissingAlertId={dismissingAlertId}
                enableGrouping
                emptyState={(
                  <Box sx={{ py: 6, textAlign: 'center' }}>
                    <NotificationsIcon sx={{ fontSize: 56, color: '#ddd' }} />
                    <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                      {t('notifications.alertsEmpty', { defaultValue: 'No actionable alerts' })}
                    </Typography>
                  </Box>
                )}
              />
            </Box>
          )}

          {tab === 1 && (
            <>
              {/* Unread list */}
              <List disablePadding>
                {unreadList.length === 0 && (
                  <Box sx={{ py: 6, textAlign: 'center' }}>
                    <NotificationsIcon sx={{ fontSize: 56, color: '#ddd' }} />
                    <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                      {t('notifications.noneUnread', { defaultValue: 'No new notifications' })}
                    </Typography>
                  </Box>
                )}

                {unreadList.map((n: any) => {
                  const colors = getSeverityColor(n.type, n.severity);
                  const isActionable = n.actionable || n.type === 'handover_delayed';
                  const { title: nTitle, message: nMessage } = translateNotificationContent(n, t);

                  return (
                    <ListItem
                      key={n.id}
                      alignItems="flex-start"
                      sx={{
                        py: 2,
                        px: 1.5,
                        mb: 1,
                        borderRadius: 3,
                        bgcolor: colors.bg,
                        border: `1px solid ${colors.border}`,
                        gap: 2,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          transform: 'translateY(-1px)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                        },
                      }}
                      secondaryAction={
                        <Stack spacing={1} alignItems="center" sx={{ mr: 0.5 }}>
                          <IconButton
                            onClick={() => handleMarkRead(n.id)}
                            sx={{
                              width: ACTION_BTN_SIZE,
                              height: ACTION_BTN_SIZE,
                              bgcolor: '#e8f5e9',
                              '&:hover': { bgcolor: '#d0f0d6' },
                            }}
                          >
                            <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 20 }} />
                          </IconButton>

                          <IconButton
                            onClick={() => handleDelete(n.id)}
                            sx={{
                              width: ACTION_BTN_SIZE,
                              height: ACTION_BTN_SIZE,
                              bgcolor: '#ffebee',
                              '&:hover': { bgcolor: '#ffdcdc' },
                            }}
                          >
                            <CloseIcon sx={{ color: '#c62828', fontSize: 20 }} />
                          </IconButton>
                        </Stack>
                      }
                    >
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            bgcolor: colors.border,
                            width: AVATAR_SIZE,
                            height: AVATAR_SIZE,
                            color: colors.text,
                          }}
                        >
                          {getIcon(n.type)}
                        </Avatar>
                      </ListItemAvatar>

                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="subtitle1" fontWeight={700}>
                              {nTitle}
                            </Typography>
                            {n.bookingNumber && (
                              <Chip
                                label={n.bookingNumber}
                                size="small"
                                sx={{ fontWeight: 700, bgcolor: `${colors.text}15`, color: colors.text, fontSize: '0.7rem' }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 0.6 }}>
                            <Typography variant="body2" sx={{ color: colors.text, mb: 1, fontWeight: 500, whiteSpace: 'pre-line' }}>
                              {nMessage}
                            </Typography>

                            {/* Actionable buttons for handover alerts */}
                            {isActionable && n.actionUrl && (
                              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => handleNotificationNavigate(n)}
                                  sx={{
                                    bgcolor: '#273B7D',
                                    fontWeight: 700,
                                    px: 2,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    '&:hover': { bgcolor: '#1d2d60' },
                                  }}
                                >
                                  {t('notifications.processNow', { defaultValue: 'Process Now' })}
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => handleMarkRead(n.id)}
                                  sx={{
                                    borderColor: colors.border,
                                    color: colors.text,
                                    fontWeight: 600,
                                    px: 2,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                  }}
                                >
                                  {t('notifications.dismiss', { defaultValue: 'Dismiss' })}
                                </Button>
                              </Stack>
                            )}

                            {/* Non-actionable but has link */}
                            {!isActionable && n.actionUrl && (
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => handleNotificationNavigate(n)}
                                sx={{ fontWeight: 600, textTransform: 'none', color: colors.text, p: 0, minWidth: 0 }}
                              >
                                {t('notifications.viewDetails', { defaultValue: 'View Details' })} ?
                              </Button>
                            )}

                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              {formatTime(n.timestamp)}
                            </Typography>
                          </Box>
                        }
                        sx={{ mr: 2 }}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </>
          )}

          {tab === 2 && (
            <>
              {/* History controls: sort + filter + clear history */}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel id="sort-label">
                    <SortIcon sx={{ mr: 0.5, verticalAlign: 'middle' }} /> {t('notifications.sort', { defaultValue: 'Sort' })}
                  </InputLabel>
                  <Select
                    labelId="sort-label"
                    value={sortBy}
                    label={t('notifications.sort', { defaultValue: 'Sort' })}
                    onChange={e => setSortBy(e.target.value as SortOption)}
                    renderValue={(v) => (v === 'newest' ? t('notifications.newest', { defaultValue: 'Newest' }) : v === 'oldest' ? t('notifications.oldest', { defaultValue: 'Oldest' }) : t('notifications.byType', { defaultValue: 'By Type' }))}
                  >
                    <SelectItem value="newest">{t('notifications.newest', { defaultValue: 'Newest' })}</SelectItem>
                    <SelectItem value="oldest">{t('notifications.oldest', { defaultValue: 'Oldest' })}</SelectItem>
                    <SelectItem value="type">{t('notifications.byType', { defaultValue: 'By Type' })}</SelectItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel id="filter-label">
                    <FilterListIcon sx={{ mr: 0.5, verticalAlign: 'middle' }} /> {t('notifications.filter', { defaultValue: 'Filter' })}
                  </InputLabel>
                  <Select
                    labelId="filter-label"
                    value={filterType}
                    label={t('notifications.filter', { defaultValue: 'Filter' })}
                    onChange={e => setFilterType(e.target.value)}
                    renderValue={(v) => (v === 'all' ? t('notifications.allTypes', { defaultValue: 'All types' }) : String(v))}
                  >
                    <SelectItem value="all">{t('notifications.allTypes', { defaultValue: 'All types' })}</SelectItem>
                    {/* Dynamically build options from historyList */}
                    {Array.from(new Set(historyList.map(h => h.type))).map((t: any) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </Select>
                </FormControl>

                <Box sx={{ flex: 1 }} />

                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteForeverIcon />}
                  onClick={() => {
                    // Clear only history (read) items
                    const all = NotificationService.getNotifications();
                    // Remove all read items
                    const readIds = all.filter((i: any) => i.read).map((i: any) => i.id);
                    readIds.forEach((id: string) => NotificationService.clearNotification(id));
                    loadNotifications();
                  }}
                >
                  {t('notifications.clearHistory', { defaultValue: 'Clear history' })}
                </Button>
              </Box>

              <Divider sx={{ mb: 2 }} />

              <List disablePadding>
                {applySortAndFilter(historyList).length === 0 && (
                  <Box sx={{ py: 6, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                      {t('notifications.noneHistory', { defaultValue: 'No history yet' })}
                    </Typography>
                  </Box>
                )}

                {applySortAndFilter(historyList).map((n: any) => {
                  const { title: nTitle, message: nMessage } = translateNotificationContent(n, t);
                  return (
                  <ListItem
                    key={n.id}
                    alignItems="flex-start"
                    sx={{
                      py: 2,
                      px: 0,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                    secondaryAction={
                      <Stack spacing={1} alignItems="center" sx={{ mr: 1 }}>
                        <IconButton
                          onClick={() => handleDeleteHistoryItem(n.id)}
                          sx={{
                            width: ACTION_BTN_SIZE,
                            height: ACTION_BTN_SIZE,
                            bgcolor: '#fff5f5',
                            '&:hover': { bgcolor: '#ffecec' },
                          }}
                        >
                          <CloseIcon sx={{ color: '#c62828', fontSize: 18 }} />
                        </IconButton>
                      </Stack>
                    }
                  >
                    <ListItemAvatar>
                      <Avatar
                        sx={{
                          bgcolor: '#fafafa',
                          width: AVATAR_SIZE,
                          height: AVATAR_SIZE,
                          color: 'text.primary',
                        }}
                      >
                        {getIcon(n.type)}
                      </Avatar>
                    </ListItemAvatar>

                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1" fontWeight={700}>
                            {nTitle}
                          </Typography>
                          <Chip label={n.type} size="small" sx={{ textTransform: 'capitalize' }} />
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 0.6 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, whiteSpace: 'pre-line' }}>
                            {nMessage}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatTime(n.timestamp)}
                          </Typography>
                        </Box>
                      }
                      sx={{ mr: 2 }}
                    />
                  </ListItem>
                  );
                })}
              </List>
            </>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default NotificationBell;

